// src/backendServices/adminManagment/api_socialNetworks.ts
//
// Réseaux sociaux vers lesquels un créateur peut publier ses vidéos. Deux
// usages : la section « Réseaux sociaux » de la fiche créateur (connexion/
// déconnexion, AdminSheet.tsx) et le déclenchement/suivi d'une publication
// depuis la fiche vidéo (VideoSheet2.tsx). Miroir, côté admin-api, du modèle
// déjà en place pour le paiement (Bank + processor par fournisseur).

import { API_URLS } from '../urls';

export interface AdminSocialNetworkStatus {
  socialNetworkId     : number;
  code                : string;
  name                : string;
  // Le réseau a une classe de traitement côté admin-api (Youtube, pour
  // l'instant) — les autres existent en base mais ne sont pas connectables.
  implemented         : boolean;
  adminSocialNetworkId: number | null;
  connected           : boolean;
  active              : boolean;
  externalAccountLabel: string | null;
}

export type SocialNetworkPublicationStatusValue = 'New' | 'Processing' | 'Success' | 'Error';
export type SocialNetworkPrivacyStatus = 'private' | 'unlisted' | 'public';

export interface SocialNetworkPublicationStatus {
  id               : number;
  socialNetworkCode: string;
  status           : SocialNetworkPublicationStatusValue;
  privacyStatus    : SocialNetworkPrivacyStatus;
  externalVideoId  : string | null;
  externalUrl      : string | null;
  errorMessage     : string | null;
  requestedAt      : string;
  publishedAt      : string | null;
}

/** Les 6 réseaux visés par la fiche vidéo, avec l'état de connexion de ce créateur pour chacun. */
export const listAdminSocialNetworks = async (adminId: number): Promise<AdminSocialNetworkStatus[]> => {
  const res = await fetch(`${API_URLS.getAdminSocialNetworks}?admin_id=${adminId}`, { cache: 'no-store' });

  const data = await res.json().catch(() => ([]));

  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);

  return Array.isArray(data) ? data : [];
};

/** URL de consentement à ouvrir (nouvel onglet) pour connecter ce réseau. */
export const getSocialNetworkAuthorizeUrl = async (adminId: number, socialNetworkCode: string): Promise<string> => {
  const res = await fetch(
    `${API_URLS.getSocialNetworkAuthorizeUrl}?admin_id=${adminId}&social_network_code=${encodeURIComponent(socialNetworkCode)}`,
    { cache: 'no-store' },
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.url) throw new Error(data.error ?? `HTTP ${res.status}`);

  return data.url as string;
};

/**
 * Deuxième chemin de connexion, propre à Instagram : réutilise la Page
 * Facebook DÉJÀ connectée par ce créateur (pas de nouvelle fenêtre de
 * consentement) plutôt que l'Instagram Login autonome de "Connecter" —
 * cf. SocialNetworkPublisher_Instagram::linkViaFacebookPage() côté admin-api.
 * Échoue si Facebook n'est pas connecté, ou si sa Page n'a aucun compte
 * Instagram professionnel lié.
 */
export const linkInstagramViaFacebook = async (adminId: number): Promise<void> => {
  const res = await fetch(`${API_URLS.linkInstagramViaFacebook}?admin_id=${adminId}`, { method: 'POST' });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
};

export const disconnectSocialNetwork = async (adminId: number, adminSocialNetworkId: number): Promise<void> => {
  const res = await fetch(
    `${API_URLS.deleteAdminSocialNetwork.replace('{id}', String(adminSocialNetworkId))}?admin_id=${adminId}`,
    { method: 'DELETE' },
  );

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
};

/** Déclenche la publication ; la commande tourne en tâche de fond côté admin-api. */
export const publishVideoToSocialNetwork = async (
  videoId: number,
  socialNetworkCode: string,
  privacyStatus: SocialNetworkPrivacyStatus,
): Promise<SocialNetworkPublicationStatus> => {
  const res = await fetch(API_URLS.publishVideoToSocialNetwork.replace('{id}', String(videoId)), {
    method : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify({ socialNetworkCode, privacyStatus }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);

  return data as SocialNetworkPublicationStatus;
};

/** Historique des tentatives de publication de cette vidéo, la plus récente en tête — pour le polling. */
export const listVideoSocialNetworkPublications = async (videoId: number): Promise<SocialNetworkPublicationStatus[]> => {
  const res = await fetch(API_URLS.getVideoSocialNetworkPublications.replace('{id}', String(videoId)), { cache: 'no-store' });

  const data = await res.json().catch(() => ([]));

  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);

  return Array.isArray(data) ? data : [];
};
